import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import { api } from '../../../../../services';
import download from 'downloadjs';
import AirTable from '../../../../../common/components/AirTable';
import AirModal from '../../../../../common/components/AirModal';
import { ExternalIcon, WarningIcon, CrashIcon, TheftIcon, HeartIcon, ConfirmIcon, DiscardIcon } from '../../../../../common/icons';

import './NoticesPanel.css';
import { Spin, Tooltip } from 'antd';
import AirButton from '../../../../../common/components/AirButton';

@inject('translationsStore', 'commonStore', 'noticesStore')
@observer
export default class NoticesPanel extends Component {
  constructor(props) {
    super(props);

    this.state = {
      reportInfoVisible: false,

      notices: []
    };
  }

  onNoticesDataAvailable = () => {
    const {
      noticesStore,
      car: { plate }
    } = this.props;
    ['crash', 'blockbeat', 'unplugged'].forEach(type => {
      noticesStore.setMultipleDismissedSamePlateType({ plate, type });
    });
  };

  loadNotices(cancelBefore = true) {
    const { car } = this.props;

    if (cancelBefore) {
      this.setState({ notices: [], loading: true });
    }

    api('getNotices', car.imei)
      .then(({ notifications }) => notifications.sort((n1, n2) => (n1.state !== n2.state ? (n1.state === 'pending' ? -1 : n2.state === 'pending' ? 1 : 0) : 0)))
      .then(notifications => this.setState({ notices: notifications, loading: false }))
      .then(notifications => {
        this.onNoticesDataAvailable();
      });
  }

  componentDidMount() {
    if (this.props.car) {
      this.loadNotices();
      this.interval = setInterval(() => {
        this.loadNotices(false);
      }, 40000);
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.car !== this.props.car && this.props.car) {
      this.loadNotices();
    }
  }

  componentWillUnmount() {
    clearInterval(this.interval);
  }

  onDownloadReport = (record, car) => {
    api('getCrashReport', car.plate)
      .then(response => {
        const fileName = `crashreport_${car.plate}_${moment(record.timestamp).format('YYYYMMDDHHmm')}.pdf`;
        download(response, fileName, 'application/pdf');
      })
      .catch(() => {
        this.setState({ reportInfoVisible: true });
      })
      .finally(() => {});
  };

  openDiscardOrConfirm = (notice, action) => {
    this.setState({ selectedNotice: notice, action, modalDiscardConfirmVisible: true });
  };

  closeDiscardOrConfirm = () => {
    this.setState({ selectedNotice: {}, action: null, modalDiscardConfirmVisible: false });
  };

  onOkDiscardConfirm = () => {
    this.closeDiscardOrConfirm();
    this.setState({ loading: true });
    api(`notice ${this.state.action}`, this.state.selectedNotice._id)
      .then(response => ({ response }))
      .catch(error => ({ error }))
      .then(({ error, response }) => {
        if (!error) {
          this.loadNotices();
        } else {
          this.setState({ loading: true });
        }
      });
  };

  render() {
    const { translationsStore, car, commonStore } = this.props;

    return (
      <div className="NoticesPanel">
        <Spin spinning={this.state.loading} tip={translationsStore.translate('notices.panel.loadingTip')}>
          <AirTable
            className="NoticesPanelTable"
            pageSize={5}
            rowKey="_id"
            dataSource={this.state.notices}
            columns={[
              {
                dataIndex: 'type',
                key: 'type',
                width: '180px',
                title: translationsStore.translate('notices.panel.columns.tipology'),
                render: type => (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    {(() => {
                      switch (type) {
                        case 'crash':
                          return (
                            <div style={{ color: 'var(--red-dtc-color)', marginRight: 10 }}>
                              <CrashIcon width="20px" height="20px" />
                            </div>
                          );
                        case 'blockbeat':
                          return (
                            <div style={{ color: 'var(--red-dtc-color)', marginRight: 10 }}>
                              <TheftIcon width="20px" height="20px" />
                            </div>
                          );
                        case 'unplugged':
                          return (
                            <div style={{ color: 'var(--red-dtc-color)', marginRight: 10 }}>
                              <HeartIcon width="20px" height="20px" />
                            </div>
                          );
                        default:
                          return (
                            <div style={{ color: 'var(--red-dtc-color)', marginRight: 10 }}>
                              <HeartIcon width="20px" height="20px" />
                            </div>
                          );
                      }
                    })()}
                    {translationsStore.translate(`notices.types.${type}`)}
                  </div>
                )
              },
              {
                align: 'center',
                dataIndex: 'timestamp',
                width: '150px',
                key: 'date',
                title: translationsStore.translate('notices.panel.columns.date'),
                render: timestamp => (
                  <div>
                    {moment(timestamp).format(commonStore.longDateFormat.L)}&nbsp;{moment(timestamp).format(commonStore.longDateFormat.LT)}
                  </div>
                )
              },

              {
                align: 'center',
                dataIndex: 'state',
                width: '100px',
                key: 'state',
                title: translationsStore.translate('notices.panel.columns.state'),
                render: state => translationsStore.translate(`notices.states.${state}`)
              },
              {
                align: 'center',
                key: 'actions',
                title: translationsStore.translate('notices.panel.downloadReport.action'),
                render: (type, record) =>
                  record.type === 'crash' ? (
                    <Tooltip title={translationsStore.translate('notices.panel.tooltip.downloadCrash')}>
                      <ExternalIcon
                        style={{ color: 'var(--selected-color)', cursor: 'pointer', width: '15px', height: '15px', marginLeft: '5px' }}
                        onClick={() => this.onDownloadReport(record, car)}
                      />
                    </Tooltip>
                  ) : (
                    ''
                  )
              },
              {
                align: 'center',
                dataIndex: 'stateMessage',
                title: translationsStore.translate('notices.panel.columns.reason')
              },
              {
                align: 'center',
                dataIndex: 'id',
                title: '',
                width: '100px',
                className: 'buttons',
                render: (id, record) => (
                  <div className={record.state}>
                    <Tooltip title={translationsStore.translate('notices.panel.tooltip.discard')}>
                      <AirButton className="discard" icon={<DiscardIcon />} onClick={() => this.openDiscardOrConfirm(record, 'discard')} />
                    </Tooltip>
                    <Tooltip title={translationsStore.translate('notices.panel.tooltip.confirm')}>
                      <AirButton className="confirm" icon={<ConfirmIcon />} onClick={() => this.openDiscardOrConfirm(record, 'confirm')} />
                    </Tooltip>
                  </div>
                )
              }
            ]}
          />
        </Spin>
        <AirModal
          className="ReportInfo"
          visible={this.state.reportInfoVisible}
          icon={<WarningIcon style={{ width: '40px', height: '40px' }} />}
          onCancel={() => {
            this.setState({ reportInfoVisible: false });
          }}
          title={translationsStore.translate('notices.panel.downloadReport.infoTitle')}
          subtitle={translationsStore.translate('notices.panel.downloadReport.fileNotAvailable')}
        />

        <AirModal
          onCancel={this.closeDiscardOrConfirm}
          onOk={this.onOkDiscardConfirm}
          className="discardConfirmModal"
          subtitle={translationsStore.translate(`notices.panel.discardConfirmModal.${this.state.action}.subtitle.${(this.state.selectedNotice || {}).type}`)}
          title={translationsStore.translate(`notices.panel.discardConfirmModal.${this.state.action}.title`)}
          visible={this.state.modalDiscardConfirmVisible}
          okButton={<AirButton size="large" light label={translationsStore.translate(`notices.panel.discardConfirmModal.${this.state.action}.okbutton`)} />}
        >
          <strong>
            <span style={{ textTransform: 'uppercase' }}>{translationsStore.translate(`notices.panel.discardConfirmModal.eventDate`)}</span>
            <span>: </span>
            <span>{moment((this.state.selectedNotice || {}).timestamp).format(commonStore.longDateFormat.L)}</span>
            <span> </span>
            <span>{moment((this.state.selectedNotice || {}).timestamp).format(commonStore.longDateFormat.LT)}</span>
          </strong>
        </AirModal>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/App/ClientListWithMap/ClientDetail/NoticesPanel/NoticesPanel.js